import {Icon} from '@iconify/react';
import React, {useCallback} from 'react';
import {Draggable} from '@hello-pangea/dnd';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {ActionCreators} from 'redux-undo';
import Button from '../../../incase-domain/components/button/Button';
import {ISlide} from '../../../incase-domain/slide';
import {AppState} from '../../../store/reducer';
import {updateSelectedSlideAction} from '../../../store/actions/slide';
import {
    selectedPresentationSelector,
    selectedPresentationSlidePreviewsSelector,
    pendingPreviewSlideIdsSelector,
} from '../../../store/selectors/presentation.selectors';
import {isSlideModifiedSelector, selectedSlideSelector} from '../../../store/selectors/slide.selectors';
import {useSlideUpdateContext} from '../../context/SetSlideUpdateContext';
import SlidePreviewContextMenuTrigger from '../contextMenu/SlidePreviewContextMenu';

interface SlidePreviewCardCardProps {
    index: number;
    displayedSlide: ISlide;
    selectedSlideId: string;
    isMultiSelected?: boolean;
    multiSelectedSlides?: ISlide[];
    onModifierClick?: (slideId: string, e: React.MouseEvent) => void;
    onPlainClick?: (slideId: string) => void;
}

function SlidePreviewCard(props: SlidePreviewCardCardProps): JSX.Element {
    const {index, displayedSlide, selectedSlideId, isMultiSelected, multiSelectedSlides, onModifierClick, onPlainClick} = props;
    const dispatch = useDispatch();
    const store = useStore<AppState>();
    const {setAndSaveSlide} = useSlideUpdateContext();

    const isPreviewPending = useSelector((state: AppState) => pendingPreviewSlideIdsSelector(state).includes(displayedSlide._id));

    const imgSrc = useSelector(
        (state: AppState) =>
            (selectedPresentationSlidePreviewsSelector(state) || []).find((preview) => preview.slideId === displayedSlide._id)?.imgSrc,
        (a, b) => a === b
    );

    const selectedSlidePayload = useSelector(
        (state: AppState) => {
            const sel = selectedSlideSelector(state);
            return sel._id === displayedSlide._id ? sel : undefined;
        },
        (a, b) => a === b
    );

    const slideForContextMenu = selectedSlidePayload ?? displayedSlide;

    const mutateModifiedSlide = useCallback(
        (cb?: () => void): void => {
            const state = store.getState();
            if (isSlideModifiedSelector(state)) {
                setAndSaveSlide(selectedSlideSelector(state), {onSuccess: cb});
            } else {
                cb?.();
            }
        },
        [setAndSaveSlide, store]
    );

    const switchSlides = useCallback(
        (id: string): void => {
            if (id === selectedSlideId) return;

            const runSwitch = (): void => {
                const selectedPresentation = selectedPresentationSelector(store.getState());
                const selectedIndex = selectedPresentation.slides.findIndex((slide) => slide._id === id);
                dispatch(updateSelectedSlideAction(selectedPresentation.slides[selectedIndex], selectedIndex, false));
            };

            mutateModifiedSlide(runSwitch);
            dispatch(ActionCreators.clearHistory());
        },
        [dispatch, mutateModifiedSlide, selectedSlideId, store]
    );

    return (
        <SlidePreviewContextMenuTrigger
            mutateSlide={mutateModifiedSlide}
            slide={slideForContextMenu}
            selectedSlides={isMultiSelected ? multiSelectedSlides : undefined}
        >
            <Draggable disableInteractiveElementBlocking key={displayedSlide._id} draggableId={displayedSlide._id} index={index}>
                {(provided) => (
                    <div
                        className="swiper-slide"
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        data-cy={`draggable-slide-${displayedSlide._id}`}
                    >
                        <Button
                            {...provided.dragHandleProps}
                            color="secondary"
                            variant={selectedSlideId === displayedSlide._id || isMultiSelected ? 'soft' : 'ghost'}
                            fullWidth
                            size="sm"
                            align="stretch"
                            data-multi-selected={isMultiSelected ? 'true' : undefined}
                            onClick={(ev) => {
                                const e = ev as unknown as React.MouseEvent | undefined;
                                if (e && (e.ctrlKey || e.metaKey || e.shiftKey) && onModifierClick) {
                                    onModifierClick(displayedSlide._id, e);
                                    return;
                                }
                                onPlainClick?.(displayedSlide._id);
                                switchSlides(displayedSlide._id);
                            }}
                            className="c-slidePreviewFooter__slide_wrapper"
                        >
                            <div className="c-slidePreviewFooter__slide">
                                <div className={`c-slidePreviewFooter__slide_image ${isPreviewPending && 'is-loading'}`}>
                                    {imgSrc ? <img src={imgSrc} alt="" data-cy="slide-image" /> : <div />}
                                    {displayedSlide.hidden && (
                                        <div className="c-slidePreviewFooter__slide_hidden">
                                            <Icon
                                                icon="ep:hide"
                                                data-cy="hidden-slide-icon"
                                                className="c-slidePreviewFooter__slide_hidden_icon"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={`c-slidePreviewFooter__slide_title c-slidePreviewFooter__slide_title--${
                                        selectedSlideId === displayedSlide._id ? 'selected' : ''
                                    }`}
                                >
                                    {displayedSlide.onAutoFavorite && (
                                        <Button
                                            variant="ghost"
                                            padded={false}
                                            size="xs"
                                            leading="solar:star-bold"
                                            color="warning"
                                            data-cy="favorite-star-icon"
                                            disablePointerEvents
                                        />
                                    )}
                                    <span data-cy="slide-number" className="c-slidePreviewFooter__slide_title_name">
                                        {index + 1}.
                                    </span>
                                    <div className="c-slidePreviewFooter__slide_title_name">{displayedSlide.name}</div>
                                </div>
                            </div>
                        </Button>
                    </div>
                )}
            </Draggable>
        </SlidePreviewContextMenuTrigger>
    );
}

export default React.memo(SlidePreviewCard);
